import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FolderLock, Loader2 } from "lucide-react";

const BG_URL = "https://images.unsplash.com/photo-1769546253924-9e23d794be53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjYXIlMjBzaG93cm9vbSUyMGludGVyaW9yfGVufDB8fHx8MTc3NjU5NDk2NXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Sprint API-PUBBLICA.3 (Parte 6): dopo il login torna alla pagina richiesta
  // (es. link diretto alla scheda veicolo aperto da non autenticati).
  const from = location.state?.from?.pathname
    ? location.state.from.pathname + (location.state.from.search || "")
    : "/";

  useEffect(() => {
    if (params.get("reason") === "expired") {
      toast.error("Sessione scaduta. Effettua nuovamente il login.");
    }
  }, [params]);

  if (user) return <Navigate to={from} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Benvenuto");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Left - form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-10" data-testid="login-brand">
            <div className="w-48 bg-white rounded-lg flex flex-col items-center pt-2 pb-3">
              <img
                src="/logo-su-chiaro.png"
                alt="KARGEST"
                className="w-44 h-auto object-contain select-none"
                draggable="false"
                data-testid="login-logo"
              />
              <div className="font-heading text-[11px] uppercase tracking-[0.18em] text-slate-500 mt-1">
                Dealer Management
              </div>
            </div>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Accedi a KARGEST</h1>
          <p className="mt-2 text-slate-500">Ogni veicolo, ogni pratica, ogni cliente. Sotto controllo.</p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                name="username"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
                placeholder="nome@azienda.it"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entra"}
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-400">
            © 2026 KARGEST · Dealer Management
          </div>
        </div>
      </div>

      {/* Right - hero image */}
      <div
        className="hidden lg:block relative"
        style={{ backgroundImage: `url(${BG_URL})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/80 via-slate-900/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-heading text-4xl font-semibold leading-tight">Ogni veicolo.<br/>Ogni pratica.<br/>Sotto controllo.</div>
          <p className="mt-4 text-white/80 max-w-md">Veicoli, anagrafiche, pratiche, margini: tutto il tuo gestionale in un solo posto.</p>
        </div>
      </div>
    </div>
  );
}
